import Fuse, { FuseResult, FuseResultMatch, IFuseOptions } from 'fuse.js';

import { Constants } from './fuseSearch.constants.js';
import { Product } from '../../product-center/product-center-table-wrapper/product-center-table-wrapper.model.js';

/**
 * Service for searching funds using Fuse.js.
 */
export class FundsSearchService {
  private readonly fuseOptions: IFuseOptions<any>;
  private fuse?: Fuse<any>;
  private flattenedData: Product[] = [];
  private readonly searchKeys = Constants.SEARCH_KEYS;
  public originalData: Product[] = [];
  public matchesFromSearch: FuseResultMatch[] = [];

  constructor() {
    this.fuseOptions = {
      ...Constants.FuseConfigOptions,
      keys: this.searchKeys,
    };
  }

  /**
   * Searches funds based on the provided search term.
   * @param searchTerm - The term to search for.
   * @returns An array of search results.
   */
  public searchFunds(searchTerm: string): FuseResult<Product>[] {
    if (!this.fuse) {
      console.warn('fuse.js has not been initialized with data');
      return [];
    }

    let results = this.fuse.search(searchTerm);

    results = results.filter((result) => {
      // Filter 1:1 matches
      if (result.matches) {
        return result.matches.some((match) => {
          if (
            Constants.ONE_TO_ONE_SEARCH_KEYS.includes(match.key ?? '') &&
            match.value?.toLowerCase() !== searchTerm.toLowerCase()
          ) {
            return false;
          }
          return true;
        });
      }
      return true;
    });

    this.matchesFromSearch = this.returnMatches(results);

    return results.map((result) => ({
      item: this.originalData[result.refIndex],
      score: result.score,
      refIndex: result.refIndex,
      matches: result.matches,
    }));
  }

  /**
   * Returns an array of unique `FuseResultMatch` object from the given `results` array.
   *
   * @param results - An array of `FuseResult` objects.
   * @returns An array of unique `FuseResultMatch` matches.
   */
  public returnMatches(results: FuseResult<Product>[]): FuseResultMatch[] {
    if (!this.fuse) {
      console.warn('fuse.js has not been initialized with data');
      return [];
    }

    const matches: FuseResultMatch[] = [];
    results.forEach((result) => {
      if (result.matches) {
        matches.push(...result.matches);
      }
    });

    return matches.filter(
      (match, index, self) =>
        index === self.findIndex((m) => m.value === match.value),
    );
  }

  public initFuse(fundData: Product[]) {
    this.originalData = fundData;
    this.flattenedData = fundData.map((product) => ({
      ...product,
      ...product.productName,
      ...product.performance,
    }));
    this.fuse = new Fuse(this.flattenedData, this.fuseOptions);
  }

  public updateFuseSearchKeys(searchKey: string) {
    const updatedFuseOptions = { ...this.fuseOptions };
    updatedFuseOptions.keys = this.searchKeys.filter(
      (key) => key?.name === searchKey,
    );
    this.fuse = new Fuse(this.flattenedData, updatedFuseOptions);
  }

  public resetFuseKeys() {
    this.initFuse(this.originalData);
  }
}
